import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { DomainManagementModal } from '../../components/admin/DomainManagementModal';
import { CreateCategoryTenantModal } from '../../components/admin/CreateCategoryTenantModal';
import { DomainResolutionTester } from '../../components/admin/DomainResolutionTester';
import { EditDomainModal } from '../../components/admin/EditDomainModal';
import { 
  Building2, 
  Plus, 
  Edit3, 
  Trash2, 
  Globe, 
  Users, 
  ShoppingCart, 
  BookOpen, 
  Vote,
  Palette,
  Settings,
  Eye,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';

interface CategoryTenant {
  id: number;
  name: string;
  email: string;
  domain_name: string;
  config: any;
  created_at: string;
  tenant_id: number;
  domains_count?: number;
}

interface DomainInfo {
  id: number;
  tenant_id: number;
  domain_name: string;
  is_primary: boolean;
  config: any;
  created_at: string;
  updated_at: string;
}

const CATEGORY_ICONS = {
  boutique: ShoppingCart,
  presentation: Building2,
  elearning: BookOpen,
  vote: Vote
};

const CATEGORY_COLORS = {
  boutique: 'bg-orange-100 text-orange-800 border-orange-200',
  presentation: 'bg-blue-100 text-blue-800 border-blue-200',
  elearning: 'bg-green-100 text-green-800 border-green-200',
  vote: 'bg-red-100 text-red-800 border-red-200'
};

export const CategoryManagement: React.FC = () => {
  const [categoryTenants, setCategoryTenants] = useState<CategoryTenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<CategoryTenant | null>(null);
  const [tenantDomains, setTenantDomains] = useState<DomainInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [showEditDomainModal, setShowEditDomainModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<DomainInfo | null>(null);

  // Charger les tenants de catégorie
  const loadCategoryTenants = async () => {
    try {
      setError('');
      setLoading(true);
      
      // Récupérer les tenants avec leur nombre de domaines
      const { data: tenants, error: tenantsError } = await supabase
        .from('tenants')
        .select(`
          id,
          name,
          email,
          domain_name,
          config,
          created_at,
          tenant_id
        `)
        .gte('id', 200) // Tenants de catégorie commencent à 200+
        .order('id');

      if (tenantsError) throw tenantsError;

      // Compter les domaines pour chaque tenant avec bypass RLS pour admin
      const tenantsWithDomains = await Promise.all(
        (tenants || []).map(async (tenant) => {
          // Utiliser RPC pour bypasser RLS
          const { data: countData, error: countError } = await supabase
            .rpc('count_tenant_domains', { tenant_id_param: tenant.id });
          
          if (countError) {
            console.error(`Erreur comptage domaines pour tenant ${tenant.id}:`, countError);
          }
          
          return {
            ...tenant,
            domains_count: countData || 0
          };
        })
      );

      setCategoryTenants(tenantsWithDomains);
    } catch (err: any) {
      setError(`Erreur lors du chargement: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Charger les domaines d'un tenant
  const loadTenantDomains = async (tenantId: number) => {
    try {
      setDomainsLoading(true);
      
      const { data: domains, error } = await supabase
        .rpc('get_tenant_domains', { tenant_id_param: tenantId });

      if (error) throw error;
      
      setTenantDomains(domains || []);
    } catch (err: any) {
      setError(`Erreur lors du chargement des domaines: ${err.message}`);
    } finally {
      setDomainsLoading(false);
    }
  };

  // Tester la résolution d'un domaine
  const testDomainResolution = async (domainName: string) => {
    try {
      const { data, error } = await supabase
        .rpc('resolve_tenant_by_domain', { domain_input: domainName });

      if (error) throw error;

      if (data && data.length > 0) {
        const result = data[0];
        alert(`✅ Résolution réussie:\nTenant ID: ${result.tenant_id}\nNom: ${result.tenant_name}\nConfiguration fusionnée disponible`);
      } else {
        alert('❌ Aucun tenant trouvé pour ce domaine');
      }
    } catch (err: any) {
      alert(`❌ Erreur de résolution: ${err.message}`);
    }
  };

  // Modifier un domaine
  const editDomain = (domain: DomainInfo) => {
    setSelectedDomain(domain);
    setShowEditDomainModal(true);
  };

  // Supprimer un domaine
  const deleteDomain = async (domainId: number) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce domaine ?')) return;
    
    try {
      const { data: success, error } = await supabase
        .rpc('delete_tenant_domain', { domain_id_param: domainId });

      if (error) throw error;

      if (success) {
        // Recharger les domaines
        if (selectedTenant) {
          await loadTenantDomains(selectedTenant.id);
          await loadCategoryTenants(); // Mettre à jour les compteurs
        }
      } else {
        throw new Error('Domaine non trouvé');
      }
    } catch (err: any) {
      setError(`Erreur lors de la suppression: ${err.message}`);
    }
  };

  useEffect(() => {
    loadCategoryTenants();
  }, []);

  const getCategoryInfo = (config: any) => {
    const category = config?.category || 'unknown';
    const Icon = CATEGORY_ICONS[category as keyof typeof CATEGORY_ICONS] || Building2;
    const colorClass = CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS] || 'bg-gray-100 text-gray-800 border-gray-200';
    
    return { category, Icon, colorClass };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Chargement des tenants de catégorie...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Building2 className="w-6 h-6 mr-2" />
            Gestion des Catégories Multi-Tenant
          </h1>
          <p className="text-gray-600 mt-1">
            Gérez les tenants par catégorie et leurs domaines associés
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Tenant
        </button>
      </div>

      {/* Messages d'erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
          <button
            onClick={() => setError('')}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Liste des tenants de catégorie */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Tenants de Catégorie ({categoryTenants.length})
            </h2>
          </div>
          
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {categoryTenants.map((tenant) => {
              const { category, Icon, colorClass } = getCategoryInfo(tenant.config);
              
              return (
                <div
                  key={tenant.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    selectedTenant?.id === tenant.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                  onClick={() => {
                    setSelectedTenant(tenant);
                    loadTenantDomains(tenant.id);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg border ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{tenant.name}</div>
                        <div className="text-sm text-gray-500">ID: {tenant.id}</div>
                        <div className="text-sm text-gray-500">{tenant.email}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {tenant.domains_count} domaine{tenant.domains_count !== 1 ? 's' : ''}
                      </div>
                      <div className="text-xs text-gray-500 capitalize">
                        {category}
                      </div>
                    </div>
                  </div>
                  
                  {tenant.domain_name && (
                    <div className="mt-2 flex items-center text-sm text-gray-600">
                      <Globe className="w-3 h-3 mr-1" />
                      {tenant.domain_name}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Détails du tenant sélectionné */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold flex items-center">
              <Globe className="w-5 h-5 mr-2" />
              Domaines du Tenant
              {selectedTenant && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({selectedTenant.name})
                </span>
              )}
            </h2>
          </div>

          {selectedTenant ? (
            <div>
                      {/* Actions */}
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowDomainModal(true)}
                    className="flex items-center px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Ajouter Domaine
                  </button>
                  <button
                    onClick={() => loadTenantDomains(selectedTenant.id)}
                    className="flex items-center px-3 py-1.5 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors"
                  >
                    <Loader2 className="w-3 h-3 mr-1" />
                    Actualiser
                  </button>
                </div>
              </div>

              {/* Liste des domaines */}
              <div className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
                {domainsLoading ? (
                  <div className="p-4 text-center">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                    <span className="text-sm text-gray-500">Chargement des domaines...</span>
                  </div>
                ) : tenantDomains.length > 0 ? (
                  tenantDomains.map((domain) => (
                    <div key={domain.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2">
                            <Globe className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{domain.domain_name}</span>
                            {domain.is_primary && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                Primaire
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => testDomainResolution(domain.domain_name)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                            title="Tester la résolution"
                          >
                            Test
                          </button>
                          <button
                            onClick={() => editDomain(domain)}
                            className="text-green-600 hover:text-green-800"
                            title="Modifier"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteDomain(domain.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {domain.config && Object.keys(domain.config).length > 0 && (
                        <div className="mt-2 text-sm text-gray-600">
                          <div className="flex items-center">
                            <Settings className="w-3 h-3 mr-1" />
                            Personnalisations: {Object.keys(domain.config).join(', ')}
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-1 text-xs text-gray-500">
                        Créé le {new Date(domain.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <Globe className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>Aucun domaine configuré</p>
                    <p className="text-sm">Ajoutez un domaine pour commencer</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Building2 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>Sélectionnez un tenant</p>
              <p className="text-sm">Choisissez un tenant pour voir ses domaines</p>
            </div>
          )}
        </div>
      </div>

      {/* Testeur de résolution de domaine */}
      <div className="mt-6">
        <DomainResolutionTester />
      </div>

      {/* Modales */}
      <CreateCategoryTenantModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onTenantCreated={loadCategoryTenants}
      />

      {selectedTenant && (
        <DomainManagementModal
          isOpen={showDomainModal}
          onClose={() => setShowDomainModal(false)}
          tenantId={selectedTenant.id}
          tenantName={selectedTenant.name}
          onDomainAdded={() => {
            loadTenantDomains(selectedTenant.id);
            loadCategoryTenants();
          }}
        />
      )}

      <EditDomainModal
        isOpen={showEditDomainModal}
        onClose={() => {
          setShowEditDomainModal(false);
          setSelectedDomain(null);
        }}
        domain={selectedDomain}
        onDomainUpdated={() => {
          if (selectedTenant) {
            loadTenantDomains(selectedTenant.id);
            loadCategoryTenants();
          }
        }}
      />

      {/* Statistiques rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(CATEGORY_ICONS).map(([category, Icon]) => {
          const count = categoryTenants.filter(t => t.config?.category === category).length;
          const colorClass = CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS];
          
          return (
            <div key={category} className={`p-4 rounded-lg border ${colorClass}`}>
              <div className="flex items-center">
                <Icon className="w-5 h-5 mr-2" />
                <div>
                  <div className="font-semibold">{count}</div>
                  <div className="text-sm capitalize">{category}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
